import { Add, Delete, Download, EyeVisible, Link, Replace } from '@factorialco/f0-react/icons/app'
import { QuotesTabsVisibilityQuery } from 'generated/resources/graphql'
import { compact } from 'lodash'

import i18n from 'modules/core/lib/i18n'
import { ResourceHeadersPropsWithActions } from 'modules/core/lib/navigation/types'

import { formattedAmount } from 'modules/banking/lib/paymentHelpers'
import {
  quoteStatusAsVariant,
  quoteTranslateStatus,
  quoteTranslateType,
} from 'modules/finance/components/Quotes/utils'
import { contactFullName } from 'modules/finance/lib/contactsUtils'

export const buildQuoteHeaderProps = (
  canSeeProjects: boolean,
  locale: string,
  quote: NonNullable<QuotesTabsVisibilityQuery['finance']['quote']>
): ResourceHeadersPropsWithActions => {
  const canCreateProject = quote._policies?.convertToProject?.can
  const projectAction =
    quote.quoteType === 'timeandmaterials' && !quote.projectId
      ? {
          label: i18n.t('finance.quotes.actions.convert_to_project'),
          icon: Replace,
          key: 'convert_to_project',
          disabled: !canCreateProject,
          tooltip: !canCreateProject ? i18n.t('finance.quotes.actions.tooltips.must_be_accepted') : undefined,
        }
      : quote.projectId && canSeeProjects
        ? {
            label: i18n.t('finance.quotes.actions.go_to_project'),
            icon: Link,
            key: 'go_to_project',
          }
        : undefined

  const canConvertToInvoice = quote.status === 'accepted'
  const invoiceAction =
    quote.status !== 'rejected' &&
    !quote.financialDocumentAssociation?.financialDocument &&
    quote._policies?.convertToInvoice?.can
      ? {
          label: i18n.t('finance.quotes.actions.create_invoice'),
          icon: Add,
          key: 'convert_to_invoice',
          disabled: !canConvertToInvoice,
          tooltip: !canConvertToInvoice ? i18n.t('finance.quotes.actions.tooltips.must_be_accepted') : undefined,
        }
      : quote.financialDocumentAssociation?.financialDocument
        ? {
            label: i18n.t('finance.quotes.actions.go_to_invoice'),
            icon: Link,
            key: 'go_to_invoice',
          }
        : undefined

  const secondaryActions = compact([
    {
      label: i18n.t('common.preview'),
      icon: EyeVisible,
      key: 'open_preview',
    },
    invoiceAction,
    projectAction,
  ])

  const otherActions = [
    {
      label: i18n.t('finance.quotes.actions.download_pdf'),
      icon: Download,
      key: 'download_quote',
    },
    quote.status === 'draft' && {
      label: i18n.t('finance.quotes.actions.delete'),
      icon: Delete,
      key: 'delete_quote',
      critical: true,
    },
  ].filter(Boolean)

  return {
    title: quote.name ?? quote.quoteNumber ?? '-',
    status: {
      label: i18n.t('finance.sale_invoices.detail.header.status'),
      text: quoteTranslateStatus(quote.status),
      variant: quoteStatusAsVariant(quote.status),
    },
    metadata: [
      {
        label: i18n.t('finance.quotes.form.client_and_deal.client'),
        value: {
          type: 'text',
          content: contactFullName(quote.client),
        },
      },
      {
        label: i18n.t('finance.quotes.form.basic_information.quote_number'),
        value: {
          type: 'text',
          content: quote.quoteNumber ?? '-',
        },
      },
      {
        label: i18n.t('finance.quotes.form.management.budget'),
        value: {
          type: 'text',
          content: quoteTranslateType(quote.quoteType),
        },
      },
      {
        label: i18n.t('finance.quotes.form.client_and_deal.deal_value'),
        value: {
          type: 'text',
          content: quote.deal
            ? formattedAmount({
                amountCents: Number(quote.deal?.estimatedAmountCents),
                currency: quote.deal?.currency ?? '',
                locale,
              })
            : '-',
        },
      },
    ],
    secondaryActions,
    otherActions,
  }
}
